import { useEffect, useState } from 'preact/hooks';
import { useStore, useCoverRunner } from './hooks';
import { coverRunner } from './lib/coverRunner';
import { Library } from './views/Library';
import { BookDetail } from './views/BookDetail';
import { AddBookSheet } from './views/Forms';
import { Journal } from './views/Journal';
import { Settings } from './views/Settings';
import { Avatar } from './components/ui';
import { Toasts, toast } from './components/toast';
import { store } from './sync/engine';
import { setToken } from './lib/http';

type Tab = 'mine' | 'family' | 'journal' | 'settings';
const TABS: Array<{ id: Tab | 'add'; label: string; icon: string }> = [
  { id: 'mine', label: 'Raftul meu', icon: '🏠' },
  { id: 'family', label: 'Familia', icon: '📚' },
  { id: 'add', label: 'Adaugă', icon: '+' },
  { id: 'journal', label: 'Jurnal', icon: '📖' },
  { id: 'settings', label: 'Setări', icon: '⚙' }
];

export function App() {
  const s = useStore();
  const [tab, setTab] = useState<Tab>(() => (sessionStorage.getItem('cc10_tab') as Tab) || 'mine');
  const [openWork, setOpenWork] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  useEffect(() => store.onError(msg => toast(msg, 'error')), []);
  useEffect(() => {
    const go = (e: Event) => setTab((e as CustomEvent).detail as Tab);
    window.addEventListener('cc10:goto', go);
    return () => window.removeEventListener('cc10:goto', go);
  }, []);
  useEffect(() => { sessionStorage.setItem('cc10_tab', tab); window.scrollTo(0, 0); }, [tab]);

  const me = s.me ? s.view.members.get(s.me.id) || s.me : null;
  const st = s.status;
  const dot = st.state === 'idle' ? (st.pending ? 'bg-amber-500' : 'bg-emerald-500') : st.state === 'syncing' ? 'bg-amber-500 animate-pulse' : 'bg-red-500';

  if (st.state === 'unauthorized') return <LoginScreen message={st.message} />;

  return (
    <div class="min-h-[100dvh]">
      <header class="sticky top-0 z-30 h-14 bg-cream-100/95 backdrop-blur border-b border-cream-300 flex items-center px-4 gap-3">
        <h1 class="font-serif text-xl font-bold text-terra-600">Casa Cărților</h1>
        <span class="text-xs text-ink-500 hidden sm:inline">{tab === 'mine' ? '· Raftul meu' : tab === 'family' ? '· Biblioteca familiei' : ''}</span>
        <span class="flex-1" />
        <button class="flex items-center gap-1.5 text-xs text-ink-500" title={st.message || 'Stare sincronizare'} onClick={() => setTab('settings')} data-testid="sync-dot">
          <span class={`h-2.5 w-2.5 rounded-full ${dot}`} />
          {st.pending > 0 && <span>{st.pending}</span>}
        </button>
        {me && <Avatar member={me} size={32} />}
      </header>

      <main class="mx-auto max-w-6xl">
        {!me && st.state === 'syncing' ? <div class="p-10 text-center text-ink-500">Se încarcă biblioteca…</div> : (
          <>
            {tab === 'mine' && <Library key="mine" scope="mine" onOpen={setOpenWork} />}
            {tab === 'family' && <Library key="family" scope="family" onOpen={setOpenWork} />}
            {tab === 'journal' && <Journal onOpen={setOpenWork} />}
            {tab === 'settings' && <Settings />}
          </>
        )}
      </main>

      <nav class="fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-cream-300 safe-bottom">
        <div class="mx-auto max-w-xl grid grid-cols-5">
          {TABS.map(t => t.id === 'add' ? (
            <button key="add" class="flex flex-col items-center justify-center py-1.5" onClick={() => setAdding(true)} aria-label="Adaugă carte">
              <span class="-mt-5 h-12 w-12 rounded-full bg-terra-500 text-white text-2xl leading-none flex items-center justify-center shadow-lg">+</span>
              <span class="text-[10px] font-semibold text-ink-500 mt-0.5">{t.label}</span>
            </button>
          ) : (
            <button key={t.id} class={`flex flex-col items-center justify-center py-2 ${tab === t.id ? 'text-terra-600' : 'text-ink-500'}`} onClick={() => setTab(t.id as Tab)} aria-current={tab === t.id}>
              <span class="text-lg leading-none">{t.icon}</span>
              <span class="text-[10px] font-semibold mt-1">{t.label}</span>
            </button>
          ))}
        </div>
      </nav>

      <BookDetail workId={openWork} onClose={() => setOpenWork(null)} />
      <AddBookSheet open={adding} onClose={() => setAdding(false)} onAdded={id => setOpenWork(id)} />
      <CoverRunPill />
      <Toasts />
    </div>
  );
}

function CoverRunPill() {
  const r = useCoverRunner();
  if (!r.running && r.done === 0) return null;
  if (!r.running) return null;
  const pct = r.total ? Math.round((r.done / r.total) * 100) : 0;
  return (
    <div class="fixed inset-x-0 z-[55] px-3" style={{ bottom: 'calc(env(safe-area-inset-bottom) + 136px)' }} data-testid="cover-run-pill">
      <div class="mx-auto max-w-md rounded-2xl bg-ink-900 text-white shadow-2xl px-4 py-3">
        <div class="flex items-center gap-3 text-sm">
          <span class="font-semibold">🖼 Coperți: {r.done}/{r.total}</span>
          <span class="text-xs opacity-80">✓ {r.ok} · ✗ {r.fail}</span>
          <span class="flex-1" />
          <button class="text-xs underline" onClick={() => coverRunner.stop()}>Oprește</button>
        </div>
        <div class="mt-2 h-1.5 rounded-full bg-white/20 overflow-hidden"><div class="h-full bg-amber-400 transition-all" style={{ width: `${pct}%` }} /></div>
        {r.current && <div class="mt-1 text-[11px] opacity-70 truncate">{r.current}</div>}
      </div>
    </div>
  );
}

function LoginScreen({ message }: { message?: string }) {
  const [val, setVal] = useState('');
  const submit = () => {
    const m = val.match(/invite=([A-Za-z0-9_-]{20,})/) || val.trim().match(/^([A-Za-z0-9_-]{20,})$/);
    if (!m) { toast('Lipește linkul complet primit (conține „#invite=”)', 'error'); return; }
    setToken(m[1]);
    location.replace('/');
  };
  return (
    <div class="min-h-[100dvh] flex flex-col items-center justify-center p-8 text-center">
      <div class="text-5xl mb-4">📚</div>
      <h1 class="font-serif text-3xl font-bold text-terra-600">Casa Cărților</h1>
      <p class="mt-2 text-ink-700">Biblioteca familiei este privată.</p>
      <p class="mt-1 text-sm text-ink-500 max-w-sm">{message || 'Deschide linkul personal primit de la Bogdan sau lipește-l mai jos.'}</p>
      <div class="mt-6 w-full max-w-sm space-y-2">
        <input class="field" placeholder="https://…/#invite=…" value={val} onInput={e => setVal((e.target as HTMLInputElement).value)} aria-label="Link de invitație" />
        <button class="btn-primary w-full" onClick={submit}>Intră</button>
      </div>
      <Toasts />
    </div>
  );
}
